import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_LINK_OR_MAP } from 'Config/Icons';
import { AssessmentSubjectBase, UpdateCommonAssessmentRequest } from 'Models/AssessmentSubject';

export interface ConfirmUnlinkAssessmentSubjectsModalProps {
    assessmentSubject: AssessmentSubjectBase;
    hideModal: () => void;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    onComplete: () => void;
}

interface FormState {
    isLoading: boolean;
    successMessage?: string;
    failureMessage?: string;
    complete: boolean;
}

/** Asks the user to confirm that `assessmentSubject` should stop using the shared due diligence assessments provided by its parent assessment subject, and unlinks the two if confirmed. Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard. */
export const ConfirmUnlinkAssessmentSubjectsModal = (props: ConfirmUnlinkAssessmentSubjectsModalProps): JSX.Element => {
    const [formState, setFormState] = useState<FormState>({ isLoading: false, complete: false });

    const onClickDelete = async () => {
        try {
            setFormState({ isLoading: true, complete: false });

            const request: UpdateCommonAssessmentRequest = {
                parent_identifier: { organization_id: props.assessmentSubject.assessment_workflow_setup.common_assessment_parent!.vendor_id, assessment_subject_id: props.assessmentSubject.assessment_workflow_setup.common_assessment_parent!.id },
                child_identifier: { organization_id: props.assessmentSubject.vendor_id, assessment_subject_id: props.assessmentSubject.id },
                linked: false,
            };
            await props.moduleApi.updateCommonAssessment(request);

            setFormState({ isLoading: false, complete: true, successMessage: 'Due diligence assessment sharing stopped.' });
            props.onComplete();
        } catch (error) {
            setFormState({ isLoading: false, failureMessage: error.message, complete: false });
        }
    };

    return (
        <Modal open header="Shared Due Diligence Assessments">
            {formState.successMessage && <Alert type="success">{formState.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}
            <Box component="form" noValidate>
                <Text>{`Are you sure you want ${props.assessmentSubject.name} to stop using the shared due diligence assessments provided by ${props.assessmentSubject.assessment_workflow_setup.common_assessment_parent!.name}?`}</Text>
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="danger" onClick={onClickDelete} fontAwesomeImage={ICON_LINK_OR_MAP} disabled={formState.isLoading || formState.complete} isLoading={formState.isLoading} loadingText="Updating...">
                        Stop using shared due diligence assessments
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
